import { cloneDeep } from 'lodash';
import { useReducer } from 'react';
import { match } from 'ts-pattern';
import { JsonLocalStorageService } from '../../../api/local-storage-service';
import { TimePoint } from '../../../api/timeranges/types';
import { Domains } from '../../../constants';
import { reducerWithPersistentWrapper } from '../../../context/hooks';
import {
  CalendarYearMonthlyTimesliderGranularity,
  CalendarYearYearlyTimesliderGranularity,
  FinancialYearQuarterlyTimesliderGranularity,
  FinancialYearYearlyTimesliderGranularity,
} from './constants';
import {
  TimePointSelectorAction,
  TimePointSelectorState,
  TimePointSelectorUpdateAction,
  TimeSliderGranularity,
} from './types';
import { getTimeRangesFromTimePoints, jsonParserReviver } from './utils';

export const timePointSelectorGranularities: TimeSliderGranularity[] = [
  CalendarYearMonthlyTimesliderGranularity,
  CalendarYearYearlyTimesliderGranularity,
  FinancialYearYearlyTimesliderGranularity,
  FinancialYearQuarterlyTimesliderGranularity,
];

export const getInitialTimePointSelectorState = (
  timePoints: TimePoint[]
): TimePointSelectorState => {
  const selectedGranularity = CalendarYearMonthlyTimesliderGranularity;
  const availableTimeRanges = getTimeRangesFromTimePoints(timePoints);
  const range = availableTimeRanges[selectedGranularity.value];
  const date = range.max;
  const initialState = {
    date,
    range,
    granularities: timePointSelectorGranularities,
    selectedGranularity,
    availableTimeRanges,
    timePoints,
  };
  return {
    ...initialState,
    initialState: cloneDeep(initialState),
  };
};

/** Builds point-selector state for a single granularity (e.g. financial year only). */
export const buildTimePointSelectorStateWithGranularity = (
  base: TimePointSelectorState,
  granularity: TimeSliderGranularity
): TimePointSelectorState => {
  const range = base.availableTimeRanges[granularity.value];
  const date = range.max;
  const granularities = [granularity];
  const initialState = {
    date,
    range,
    granularities,
    selectedGranularity: granularity,
    availableTimeRanges: base.availableTimeRanges,
    timePoints: base.timePoints,
  };
  return {
    ...initialState,
    initialState: cloneDeep(initialState),
  };
};

export const useTimePointSelectorWithPersistence = (
  domain: Domains,
  init: TimePointSelectorState
) => {
  const persistenceKey = `${domain}-timepointselector-v1`;
  const localStorageService =
    new JsonLocalStorageService<TimePointSelectorState>(
      persistenceKey,
      jsonParserReviver
    );
  const storedItems: TimePointSelectorState | null = localStorageService.get();
  const selectedGranularity =
    storedItems?.selectedGranularity ?? init.selectedGranularity;
  const range = init.availableTimeRanges[selectedGranularity.value];
  const initialState = {
    ...init,
    selectedGranularity,
    range,
    date: storedItems?.date ?? init.date,
  };
  const state = {
    ...initialState,
    initialState: cloneDeep(initialState),
  };
  return useReducer(
    reducerWithPersistentWrapper(reducer, localStorageService),
    state
  );
};

export const useTimePointSelector = (init: TimePointSelectorState) => {
  return useReducer(reducer, init);
};

export const reducer = (
  state: TimePointSelectorState,
  action: TimePointSelectorAction
): TimePointSelectorState => {
  return match(action)
    .with({ type: 'reset' }, () => {
      const newState = state.initialState;
      return { ...newState, initialState: newState };
    })
    .with({ type: 'update-point' }, (a: TimePointSelectorUpdateAction) => {
      return {
        ...state,
        date: a.date,
      };
    })
    .exhaustive();
};
